import React, { useState, useEffect } from 'react';
import { TextField, Flex, Text, TextArea, Button, Select } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiContentSave } from '@mdi/js';
import { api } from '../services/api';
import { useStore } from '../store/store';
import { useParams } from 'react-router-dom';

export const SidebarSettings = () => {
    const { novelId: urlNovelId } = useParams();
    const [state, actions] = useStore();
    const [settings, setSettings] = useState({
        tone: '',
        direct_speech: 'normal',
        inner_monologue: true
    });
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    
    // Use either the URL novelId or get it from the current novel
    const novelId = urlNovelId || state.currentNovel?.id;

    useEffect(() => {
        if (state.currentNovel) {
            const isNonFiction = state.currentNovel.type === 'non-fiction';
            
            setSettings({
                tone: state.currentNovel.tone || '',
                direct_speech: state.currentNovel.direct_speech || 'normal',
                inner_monologue: isNonFiction 
                    ? state.currentNovel.direct_speech || ''  // For non-fiction, use direct_speech as the "Other Instructions"
                    : state.currentNovel.inner_monologue !== undefined 
                        ? Boolean(state.currentNovel.inner_monologue) 
                        : true
            });
        }
    }, [state.currentNovel]);

    const handleSave = async () => {
        try {
            setIsSaving(true);
            setMessage('');

            // Check if we have a valid novelId
            if (!novelId) {
                setMessage('Error: Novel ID is undefined');
                setIsSaving(false);
                return;
            }

            const isNonFiction = state.currentNovel?.type === 'non-fiction';
            
            // Prepare the data to be sent to the API
            const adjustedSettings = {
                tone: settings.tone,
                direct_speech: isNonFiction ? settings.inner_monologue : settings.direct_speech, // For non-fiction, use inner_monologue as direct_speech
                inner_monologue: isNonFiction ? settings.inner_monologue : (settings.inner_monologue ? 1 : 0)
            };

            const response = await api.updateNovelSettings(novelId, adjustedSettings);
            
            if (response.success) {
                // Update the novel in the application state
                actions.setCurrentNovel({
                    ...state.currentNovel,
                    tone: settings.tone,
                    direct_speech: settings.direct_speech,
                    inner_monologue: settings.inner_monologue
                });
                
                setMessage('Settings saved successfully');
                
                // Clear message after 3 seconds
                setTimeout(() => {
                    setMessage('');
                }, 3000);
            } else {
                setMessage('Error saving settings: ' + (response.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error saving novel settings:', error);
            setMessage('Error saving settings: ' + (error.message || 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const isNonFiction = state.currentNovel?.type === 'non-fiction';

    // Show an error message if no novel is loaded
    if (!novelId) {
        return (
            <div className="sidebar-sidebar">
                <Text size="5" weight="bold" mb="4">Novel Settings</Text>
                <div className="sidebar-form">
                    <Text color="red">
                        No novel is currently selected or the novel ID could not be determined.
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div className="sidebar-sidebar">
            <Text size="5" weight="bold" mb="4">Novel Settings</Text>

            <div className="sidebar-form">
                <Flex direction="column" align="start" mb="4">
                    <Text size="2" className="sidebar-form-textInput-label">Tone</Text>
                    <TextArea 
                        className="sidebar-form-textInput"
                        placeholder="Describe the tone of your novel..."
                        value={settings.tone || ''}
                        variant="soft"
                        onChange={(e) => setSettings({...settings, tone: e.target.value})}
                        disabled={state.readOnly}
                        style={{width: '100%', height: '80px'}}
                    />
                    <Text size="1" color="gray" style={{marginTop: '8px', fontStyle: 'italic'}}>
                        Examples: formal, casual, humorous, dark, poetic
                    </Text>
                </Flex>


                {/* <Flex direction="column" align="start" mb="4">
                    <Text size="2" className="sidebar-form-textInput-label">Direct Speech Style</Text>
                    <Select.Root 
                        value={settings.direct_speech || 'normal'}
                        onValueChange={(value) => setSettings({...settings, direct_speech: value})}
                        disabled={state.readOnly}
                    >
                        <Select.Trigger 
                            className="sidebar-form-textInput" 
                            variant="soft"
                            style={{width: '100%', marginBottom: '8px'}}
                        />
                        <Select.Content>
                            <Select.Item value="none">None</Select.Item>
                            <Select.Item value="normal">Normal</Select.Item>
                            <Select.Item value="a lot">A lot</Select.Item>
                        </Select.Content>
                    </Select.Root>
                    <Text size="1" color="gray" style={{fontStyle: 'italic'}}>
                        Controls how much dialogue appears in generated content
                    </Text>
                </Flex> */}

                <Flex direction="column" align="start" mb="4">
                    <Text size="2" className="sidebar-form-textInput-label">
                        {isNonFiction ? 'Other Instructions' : 'Inner Monologue'}
                    </Text>
                    {isNonFiction ? (
                        <TextArea 
                            className="sidebar-form-textInput"
                            placeholder="Additional instructions for your non-fiction content..."
                            value={settings.inner_monologue || ''}
                            variant="soft"
                            onChange={(e) => setSettings({...settings, inner_monologue: e.target.value})}
                            disabled={state.readOnly}
                            style={{width: '100%', height: '80px'}}
                        />
                    ) : (
                        <Select.Root 
                            value={settings.inner_monologue ? 'true' : 'false'}
                            onValueChange={(value) => setSettings({...settings, inner_monologue: value === 'true'})}
                            disabled={state.readOnly}
                        >
                            <Select.Trigger 
                                className="sidebar-form-textInput" 
                                variant="soft"
                                style={{width: '100%', marginBottom: '8px'}}
                            />
                            <Select.Content>
                                <Select.Item value="true">Enabled</Select.Item>
                                <Select.Item value="false">Disabled</Select.Item>
                            </Select.Content>
                        </Select.Root>
                    )}
                    <Text size="1" color="gray" style={{fontStyle: 'italic', marginTop: '8px'}}>
                        {isNonFiction 
                            ? 'Additional writing instructions for your non-fiction content' 
                            : 'Controls whether characters express their thoughts internally'}
                    </Text>
                </Flex>

                {!state.readOnly && (
                    <Button 
                        color="green"
                        size="2"
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{
                            width: '100%', 
                            marginTop: '20px', 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Icon path={mdiContentSave} size={0.9} style={{marginRight: '8px'}} />
                        {isSaving ? 'Saving...' : 'Save Settings'}
                    </Button>
                )}

                {message && (
                    <Text 
                        size="2" 
                        style={{
                            marginTop: '15px', 
                            padding: '10px', 
                            backgroundColor: message.includes('Error') ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 0, 0.1)',
                            borderRadius: '4px',
                            width: '100%',
                            textAlign: 'center'
                        }}
                    >
                        {message}
                    </Text>
                )}
            </div>
        </div>
    );
};

export default SidebarSettings;
